import React, { useState } from 'react';
import { PriceArea } from '../types';
import { getPriceAreaInfo } from '../utils/api';
import {
  getVatMultiplier,
  NORGESPRIS_EX_VAT,
  NORGESPRIS_MONTHLY_KWH_CAP,
  NORGESPRIS_CABIN_MONTHLY_KWH_CAP
} from '../utils/subsidyCalculator';
import { MapPin, Calculator, Info, Zap, CheckCircle, Award } from 'lucide-react';

// Average spot price per calendar month, øre/kWh excl. VAT, for the last 12 months (Oct 2025 – Sep 2026),
// and the average price after strømstøtte, calculated hour by hour with the threshold in force
// (75 øre until 31.12.2025, 77 øre from 1.1.2026). Source: hourly prices from hvakosterstrommen.no.
// Index 0 = January.
const LAST_12_MONTHS: Record<PriceArea, { spot: number[]; afterSubsidy: number[] }> = {
  NO1: {
    spot: [127.3, 125.2, 117.7, 111.1, 106.5, 82.8, 109.0, 132.3, 132.7, 58.7, 92.3, 81.5],
    afterSubsidy: [81.6, 81.8, 80.6, 77.3, 72.9, 67.9, 74.6, 79.8, 77.3, 57.0, 72.5, 72.8]
  },
  NO2: {
    spot: [123.1, 114.2, 116.0, 103.4, 108.4, 105.3, 115.2, 141.6, 141.1, 69.5, 94.0, 82.6],
    afterSubsidy: [81.2, 80.7, 79.5, 74.1, 73.3, 70.9, 75.0, 80.6, 77.0, 63.3, 73.9, 72.8]
  },
  NO3: {
    spot: [119.4, 119.7, 78.1, 103.3, 79.0, 59.9, 73.8, 100.7, 107.1, 28.6, 54.3, 58.7],
    afterSubsidy: [79.9, 80.5, 72.0, 74.9, 64.3, 57.2, 68.1, 75.0, 76.3, 28.3, 47.1, 54.7]
  },
  NO4: {
    spot: [83.2, 97.7, 18.7, 14.4, 39.0, 28.4, 17.2, 16.5, 36.0, 5.3, 35.7, 31.7],
    afterSubsidy: [60.0, 68.0, 18.6, 14.3, 33.4, 27.5, 17.2, 14.5, 33.3, 5.3, 28.0, 28.4]
  },
  NO5: {
    spot: [125.2, 123.5, 119.7, 122.7, 109.9, 68.7, 111.0, 124.8, 132.4, 56.0, 88.4, 78.2],
    afterSubsidy: [81.8, 81.7, 81.3, 81.5, 76.7, 63.4, 78.1, 79.1, 78.2, 55.7, 70.9, 72.6]
  }
};
const DATA_PERIOD = 'oktober 2025 – september 2026';

// Typical share of yearly consumption per month for a household with electric heating (sums to 1)
const MONTHLY_PROFILE = [0.13, 0.12, 0.11, 0.085, 0.06, 0.045, 0.04, 0.045, 0.06, 0.085, 0.105, 0.115];

const NORGESPRIS_OPTIONS = {
  '2026': { exVat: NORGESPRIS_EX_VAT, label: '2026: 40 øre/kWh' },
  '2027': { exVat: 0.45, label: '2027: 45 øre/kWh (forslag)' }
} as const;
type NorgesprisYear = keyof typeof NORGESPRIS_OPTIONS;
type PropertyType = 'bolig' | 'fritidsbolig';

interface NorgesPrisCalculatorProps {
  area: PriceArea;
  onAreaChange: (area: PriceArea) => void;
}

const NorgesPrisCalculator: React.FC<NorgesPrisCalculatorProps> = ({ area, onAreaChange }) => {
  const [yearlyUsage, setYearlyUsage] = useState<number>(16000);
  const [propertyType, setPropertyType] = useState<PropertyType>('bolig');
  const [norgesprisYear, setNorgesprisYear] = useState<NorgesprisYear>('2026');
  const [result, setResult] = useState<{
    norgesPrisCost: number;
    currentCost: number;
    savings: number;
    recommendation: string;
  } | null>(null);

  const areas = getPriceAreaInfo();
  const vat = getVatMultiplier(area);
  const alternativeLabel = propertyType === 'bolig' ? 'Strømstøtte' : 'Spotpris';

  const calculateCosts = () => {
    // Track calculator usage with Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'norgespris_calculation', {
        event_category: 'Calculator',
        event_label: 'Norgespris Calculator Used',
        custom_parameters: {
          yearly_usage: yearlyUsage,
          price_area: area,
          property_type: propertyType,
          norgespris_year: norgesprisYear,
          calculation_timestamp: new Date().toISOString()
        }
      });
    }

    const { spot, afterSubsidy } = LAST_12_MONTHS[area];
    const norgespris = NORGESPRIS_OPTIONS[norgesprisYear].exVat;
    const cap = propertyType === 'bolig' ? NORGESPRIS_MONTHLY_KWH_CAP : NORGESPRIS_CABIN_MONTHLY_KWH_CAP;

    // Both schemes only cover consumption up to the monthly cap; the rest is billed at spot price.
    // Strømstøtte does not apply to fritidsboliger (unless someone lives there permanently).
    let norgesPrisCost = 0;
    let currentCost = 0;
    MONTHLY_PROFILE.forEach((share, month) => {
      const kWh = yearlyUsage * share;
      const covered = Math.min(kWh, cap);
      const above = kWh - covered;
      const spotNok = spot[month] / 100;
      const subsidisedNok = propertyType === 'bolig' ? afterSubsidy[month] / 100 : spotNok;
      norgesPrisCost += (covered * norgespris + above * spotNok) * vat;
      currentCost += (covered * subsidisedNok + above * spotNok) * vat;
    });
    norgesPrisCost = Math.round(norgesPrisCost);
    currentCost = Math.round(currentCost);

    const savings = currentCost - norgesPrisCost;

    const basis = `med strømprisene fra ${DATA_PERIOD}`;
    let recommendation;
    if (savings > 1000) {
      recommendation = `Norgespris ville vært klart lønnsomt for deg ${basis}`;
    } else if (savings > 0) {
      recommendation = `Norgespris ville gitt en liten besparelse ${basis}`;
    } else if (savings > -1000) {
      recommendation = `Strømstøtte ville vært marginalt bedre ${basis}`;
    } else {
      recommendation = `Strømstøtte ville vært klart mest lønnsomt ${basis}`;
    }

    setResult({
      norgesPrisCost,
      currentCost,
      savings,
      recommendation
    });
  };

  return (
    <div className="bg-white dark:bg-dark-card rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-dark-border">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 text-white">
        <div className="flex items-center">
          <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg mr-3">
            <Calculator className="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">
              Norgespris-kalkulator 2026/2027
            </h2>
            <p className="text-blue-100 opacity-90 text-sm">
              Test om fastpris på {area === 'NO4' ? '40 øre/kWh (uten mva. i Nord-Norge)' : '40 øre/kWh (50 øre med mva.)'} lønner seg for deg
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Input Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Årlig strømforbruk (kWh)
            </label>
            <input
              type="number"
              value={yearlyUsage}
              onChange={(e) => setYearlyUsage(Number(e.target.value))}
              className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              placeholder="16000"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Leilighet ~8 000–12 000, enebolig ~16 000–25 000
            </p>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Prisområde
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <MapPin className="h-4 w-4 text-gray-400" />
              </div>
              <select
                value={area}
                onChange={(e) => onAreaChange(e.target.value as PriceArea)}
                className="block w-full pl-10 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white appearance-none"
              >
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Type bolig
            </label>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value as PropertyType)}
              className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            >
              <option value="bolig">Bolig (maks 5000 kWh/mnd)</option>
              <option value="fritidsbolig">Fritidsbolig/hytte (maks 1000 kWh/mnd)</option>
            </select>
            {propertyType === 'fritidsbolig' && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Fritidsboliger får ikke strømstøtte, så alternativet er full spotpris.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Norgespris
            </label>
            <select
              value={norgesprisYear}
              onChange={(e) => setNorgesprisYear(e.target.value as NorgesprisYear)}
              className="block w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            >
              {(Object.keys(NORGESPRIS_OPTIONS) as NorgesprisYear[]).map((year) => (
                <option key={year} value={year}>
                  {NORGESPRIS_OPTIONS[year].label} uten mva.
                </option>
              ))}
            </select>
            {norgesprisYear === '2027' && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Foreslått i statsbudsjettet for 2027. Stortinget vedtar budsjettet i desember.
              </p>
            )}
          </div>
        </div>

        {/* Calculate Button */}
        <button
          onClick={calculateCosts}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium flex items-center justify-center"
        >
          <Calculator className="w-4 h-4 mr-2" />
          Beregn besparelse
        </button>

        {/* Quick recommendation based on area */}
        <div className={`p-4 rounded-lg border ${
          area === 'NO4'
            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}>
          <div className="flex items-center">
            {area === 'NO4' ? (
              <Info className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
            )}
            <div>
              <p className={`font-medium ${
                area === 'NO4'
                  ? 'text-yellow-800 dark:text-yellow-200'
                  : 'text-green-800 dark:text-green-200'
              }`}>
                {area === 'NO4'
                  ? 'Usikkert om Norgespris lønner seg i Nord-Norge'
                  : 'Norgespris har lønt seg for de fleste i ditt område det siste året'
                }
              </p>
              <p className={`text-sm ${
                area === 'NO4'
                  ? 'text-yellow-700 dark:text-yellow-300'
                  : 'text-green-700 dark:text-green-300'
              }`}>
                {area === 'NO4'
                  ? 'Spotprisen var under 40 øre i mange måneder, men høy i vinter. Test med ditt forbruk.'
                  : 'Strømprisen etter strømstøtte var i snitt godt over 40 øre/kWh uten mva.'
                }
              </p>
            </div>
          </div>
        </div>
        {result && (
          <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-300">
            {/* Main Result */}
            <div className={`rounded-xl p-4 text-white shadow-lg ${
              result.savings > 0 
                ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-600'
            }`}>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  {result.savings > 0 ? (
                    <Award className="h-5 w-5 mr-2" />
                  ) : (
                    <Info className="h-5 w-5 mr-2" />
                  )}
                  <h3 className="text-lg font-bold">
                    {result.savings > 0 ? 'Norgespris lønner seg!' : `${alternativeLabel} lønner seg!`}
                  </h3>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-xs opacity-90 mb-1">Du sparer årlig</div>
                    <div className="text-2xl font-bold">
                      {Math.abs(result.savings).toLocaleString('nb-NO')} kr
                    </div>
                    <div className="text-xs opacity-75">
                      med {result.savings > 0 ? 'Norgespris' : alternativeLabel.toLowerCase()}
                    </div>
                  </div>
                  
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <div className="text-xs opacity-90 mb-1">Du sparer månedlig</div>
                    <div className="text-2xl font-bold">
                      {Math.round(Math.abs(result.savings) / 12).toLocaleString('nb-NO')} kr
                    </div>
                    <div className="text-xs opacity-75">
                      i gjennomsnitt
                    </div>
                  </div>
                </div>
                
                <p className="text-sm mt-3 opacity-90">{result.recommendation}.</p>
                <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                  <div className="bg-white/10 rounded-lg p-2">
                    <div className="text-xs opacity-80">{propertyType === 'bolig' ? 'Med strømstøtte' : 'Med spotpris'}</div>
                    <div className="font-bold">{result.currentCost.toLocaleString('nb-NO')} kr/år</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2">
                    <div className="text-xs opacity-80">Med Norgespris</div>
                    <div className="font-bold">{result.norgesPrisCost.toLocaleString('nb-NO')} kr/år</div>
                  </div>
                </div>

                {/* Call to action based on result */}
                <div className="mt-4 pt-4 border-t border-white/20">
                  {result.savings > 0 ? (
                    <a
                      href="https://minside.elhub.no"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-6 py-2 bg-white text-green-600 rounded-lg hover:bg-gray-50 transition-colors font-bold"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Bestill på elhub.no
                    </a>
                  ) : (
                    <p className="text-white/90 text-sm">
                      {propertyType === 'bolig' ? 'Fortsett med strømstøtte for best pris' : 'Spotpris uten Norgespris gir best pris'}
                    </p>
                  )}
                  <p className="text-xs mt-3 opacity-80">
                    Alle som vil ha Norgespris i 2027, må bestille på nytt. Bestillingen åpner etter at Stortinget har vedtatt statsbudsjettet i desember.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NorgesPrisCalculator;